import { apiClient } from '@/shared/lib/api/apiClient';
import type { LoginResponse } from '@/shared/lib/types';

export interface MfaSetupResponse {
  secret: string;
  otpAuthUri: string;
  issuer: string;
  accountName: string;
}

export interface MfaConfirmResponse {
  auth: LoginResponse | null;
  recoveryCodes: string[];
}

export interface MfaStatus {
  enabled: boolean;
  enforcedAt: string | null;
  recoveryCodesRemaining: number;
  enforcedByPolicy: boolean;
}

export interface MfaOverviewItem {
  id: string;
  fullName: string;
  email: string;
  tipoUsuario: string;
  mfaEnabled: boolean;
  mfaEnforcedAt: string | null;
  lastLoginAt: string | null;
  isActive: boolean;
}

export interface MfaOverview {
  totalUsers: number;
  withMfa: number;
  withoutMfa: number;
  users: MfaOverviewItem[];
}

interface ApiEnvelope<T> { data: T; success?: boolean; message?: string }

function unwrap<T>(response: any): T {
  if (response?.data && 'data' in response.data) return response.data.data as T;
  if (response?.data) return response.data as T;
  return response as T;
}

const BASE = '/local-auth';

export const MfaApiService = {
  async verify(mfaSessionToken: string, code: string): Promise<LoginResponse> {
    const r = await apiClient.post<ApiEnvelope<LoginResponse>>(`${BASE}/mfa/verify`, {
      mfaSessionToken,
      code,
    });
    return unwrap<LoginResponse>(r);
  },

  async startSetupChallenge(mfaSessionToken: string): Promise<MfaSetupResponse> {
    const r = await apiClient.post<ApiEnvelope<MfaSetupResponse>>(`${BASE}/mfa/setup/start`, {
      mfaSessionToken,
    });
    return unwrap<MfaSetupResponse>(r);
  },

  async confirmSetupChallenge(
    mfaSessionToken: string,
    secret: string,
    code: string
  ): Promise<MfaConfirmResponse> {
    const r = await apiClient.post<ApiEnvelope<MfaConfirmResponse>>(`${BASE}/mfa/setup/confirm`, {
      mfaSessionToken,
      secret,
      code,
    });
    return unwrap<MfaConfirmResponse>(r);
  },

  async getStatus(): Promise<MfaStatus> {
    const r = await apiClient.get<ApiEnvelope<MfaStatus>>(`${BASE}/mfa/status`);
    return unwrap<MfaStatus>(r);
  },

  async startEnable(): Promise<MfaSetupResponse> {
    const r = await apiClient.post<ApiEnvelope<MfaSetupResponse>>(`${BASE}/mfa/enable/start`);
    return unwrap<MfaSetupResponse>(r);
  },

  async confirmEnable(secret: string, code: string): Promise<MfaConfirmResponse> {
    const r = await apiClient.post<ApiEnvelope<MfaConfirmResponse>>(`${BASE}/mfa/enable/confirm`, {
      secret,
      code,
    });
    return unwrap<MfaConfirmResponse>(r);
  },

  async disable(currentPassword: string, code: string): Promise<void> {
    await apiClient.post<void>(`${BASE}/mfa/disable`, { currentPassword, code });
  },

  async regenerateRecoveryCodes(code: string): Promise<string[]> {
    const r = await apiClient.post<ApiEnvelope<string[]>>(
      `${BASE}/mfa/recovery-codes/regenerate`,
      { code }
    );
    return unwrap<string[]>(r);
  },

  async getOverview(): Promise<MfaOverview> {
    const r = await apiClient.get<ApiEnvelope<MfaOverview>>('/users/mfa-overview');
    return unwrap<MfaOverview>(r);
  },

  async adminDisable(targetUserId: string): Promise<void> {
    await apiClient.post<void>(`/users/${targetUserId}/mfa/disable`);
  },

  async tenantAdminResetClientMfa(targetUserId: string, code: string): Promise<void> {
    await apiClient.post<void>(`/users/${targetUserId}/mfa/tenant-admin-reset`, { code });
  },
};
